import { ReactElement, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/user.context';
import Button from '../controls/Button';
import { useAuth } from '../context/auth.context';
import { useData } from '../context/data.context';
import { ROLE_BY_ID } from '../constants/global-constants';

/**
 * User Component will render the drop down with user information and some links.
 * @param props - Properties of ProfileDropDown component.
 * @param props.onLogout - Logout function.
 * @param props.onSetting - OnClick of Setting page.
 * @returns A react Component.
 */
function ProfileDropDown(props: { onLogout: () => void; onSetting: () => void }): ReactElement {
  const { onLogout, onSetting } = props;
  const { profile_picture: profilePicture, subscription_type: subscriptionType } =
    useContext(UserContext);
  const { currentUser } = useData();
  const menuItems = [
    { menu_icon: 'settings', menu_name: 'Settings', id: 1, action: onSetting },
    { menu_icon: 'logout', menu_name: 'Log out', id: 2, action: onLogout }
  ];
  return (
    <div className="rounded-lg border border-gray-100 bg-white dark:border-background-700 dark:bg-background-800 z-10 shadow-xs absolute right-2 mt-72">
      <div className="px-4 py-3 pr-40 border-b dark:border-b-gray-900 border-b-gray-200 flex">
        {/* <div className="rounded-full bg-success-500 w-3 h-3 absolute top-[3rem] left-[3.5rem] ring-2 ring-white" /> */}
        <img src={profilePicture} className="h-10 w-10" alt="PROFILE PIC" />
        <div className="pl-3">
          <p className="text-gray-800 text-left dark:text-gray-300 text-sm font-bold whitespace-nowrap overflow-wrap overflow-ellipsis">
            {currentUser?.firstName}
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm font-normal text-start">
            {currentUser?.role ? ROLE_BY_ID[currentUser.role] : '-'}
          </p>
        </div>
      </div>
      <div className="mx-4 py-3 flex justify-between items-center border-b dark:border-b-gray-900 border-b-gray-200">
        <p className="text-gray-400 dark:text-gray-500 text-sm font-medium">{subscriptionType}</p>
        {subscriptionType === 'Standard' && (
          <Button name="Upgrade" theme="primary-purple" size="sm" />
        )}
      </div>
      {menuItems.map((currentMenuItem) => (
        <button
          type="button"
          key={currentMenuItem.id}
          onClick={() => currentMenuItem.action && currentMenuItem.action()}
          className="px-5 py-4  flex items-center text-gray-400 hover:text-gray-700 dark:text-gray-500 text-sm font-medium cursor-pointer dark:hover:text-gray-300 group"
        >
          <span className="material-icons-outlined text-gray-300 group-hover:text-gray-800 dark:text-gray-700 pr-2 dark:group-hover:text-gray-400">
            {currentMenuItem.menu_icon}
          </span>
          {currentMenuItem.menu_name}
        </button>
      ))}
    </div>
  );
}

/**
 * Component representing the user's username and profile picture.
 * @returns - A UserInformation Component.
 */
export default function UserInformation(): ReactElement {
  const [dropDownVisible, SetDropDownVisible] = useState(false);
  const { profile_picture: profilePicture } = useContext(UserContext);
  const { authUser, logout } = useAuth();
  const { currentUser } = useData();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLButtonElement | null>(null);
  /**
   * HandelOutsideClick Function Handles clicks outside the dropdown to close it.
   * @param  event - The click event.
   */
  const handleOutsideClick = (event: MouseEvent): void => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      SetDropDownVisible(false);
    }
  };

  useEffect(() => {
    if (dropDownVisible) {
      document.addEventListener('click', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [dropDownVisible]);

  /**
   * Logout function.
   */
  const onLogout = (): void => {
    if (logout) {
      logout();
    }
  };
  /**
   * Navigate toSetting function.
   */
  const onSetting = (): void => {
    navigate('/settings');
  };
  return (
    <button
      type="button"
      tabIndex={0}
      disabled={!authUser}
      onClick={() => SetDropDownVisible(!dropDownVisible)}
      onKeyDown={() => SetDropDownVisible(!dropDownVisible)}
      className="text-border flex gap-2 p-1 items-center"
      ref={dropdownRef}
    >
      <p className="text-gray-400 text-sm font-medium ">{currentUser?.firstName}</p>
      <div className="w-10 h-10">
        <img
          src={profilePicture}
          className={`hover:ring-4  ring-background-700 rounded-full h-10 w-10 cursor-pointer ${
            dropDownVisible && 'ring-4'
          }`}
          alt=""
        />
      </div>
      {dropDownVisible && <ProfileDropDown onLogout={onLogout} onSetting={onSetting} />}
    </button>
  );
}
